import { finleyAssertUnreachable } from '@finley-technologies/common';

import {
  FundingRequestStatusEnumGQLType,
  LoanFacilityDataGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import * as types from './types';

type LoanActivityStatus =
  | 'approved'
  | 'rejected'
  | 'submitted'
  | 'pending'
  | 'canceled'
  | 'unknown'
  | 'paid';

export type LoanActivity = {
  activityId?: string;
  amount: number;
  effectiveDate: string;
  facility: LoanFacilityDataGQLType;
  type: string;
  isEdited: boolean;
  status: LoanActivityStatus;
};

const _fundingRequestStatusToActivityStatus = ({
  status,
}: {
  status: FundingRequestStatusEnumGQLType;
}) => {
  switch (status) {
    case 'approved':
      return 'approved';
    case 'rejected':
      return 'rejected';
    case 'submitted':
      return 'submitted';
    case 'unsubmitted':
    case 'pending':
      return 'pending';
    case 'expired':
    case 'canceled':
      return 'canceled';
    case 'canceledLoanEdit':
    case 'pendingLoanEdit':
    case 'overriddenFromLoanEdit':
      return 'unknown'; // need to clarify what the loan edit statuses are
    default:
      return finleyAssertUnreachable(status);
  }
};

// @jendevelops need to pipe in actual invoice status to deal with this
const _invoiceStatusToActivityStatus = ({
  invoice,
}: {
  invoice: NonNullable<types.ResolvedLoanForEditLoan['invoices']>;
}) => {
  if (invoice.mostRecentPaymentDate) {
    return 'paid';
  }
  return 'unknown';
};

const getFundingRequestLoanActivity = ({
  isEdited = false,
  facility,
  fundingRequest: {
    fundingRequestId,
    fundingRequestStatus,
    originationDate,
    totalRequestedAmount,
  },
}: {
  isEdited?: boolean;
  facility: types.ResolvedLoanForEditLoan['facility'];
  fundingRequest: types.ResolvedLoanForEditLoan['funding']['requests'][number];
}): LoanActivity => {
  return {
    activityId: fundingRequestId,
    amount: totalRequestedAmount,
    // @jendevelops TODO: need to pipe in the actual date of the funding request (probably not origination but request date)
    effectiveDate: originationDate,
    facility,
    type: 'Funding Request',
    isEdited,
    status: _fundingRequestStatusToActivityStatus({
      status: fundingRequestStatus,
    }),
  };
};

const getInvoiceLoanActivity = ({
  isEdited = false,
  facility,
  invoice,
}: {
  facility: types.ResolvedLoanForEditLoan['facility'];
  invoice: NonNullable<types.ResolvedLoanForEditLoan['invoices']>;
  isEdited?: boolean;
}): LoanActivity | null => {
  // @jendevelops once invoice status piped in, no need to return early
  if (!invoice.mostRecentPaymentDate) {
    return null;
  }

  return {
    activityId: undefined,
    amount: 0, // @jendevelops TODO: pipe in invoices
    effectiveDate: invoice.mostRecentPaymentDate,
    facility,
    type: 'Payment', // @jendevelops are there other types other than payment?
    isEdited,
    status: _invoiceStatusToActivityStatus({
      invoice,
    }),
  };
};

export const getInitialLoanActivity = ({
  resolvedLoan,
}: {
  resolvedLoan: types.ResolvedLoanForEditLoan;
}) => {
  const {
    funding: { requests: fundingRequests },
    invoices,
  } = resolvedLoan;

  const fundingRequestActivities: (LoanActivity | null)[] = fundingRequests.map(
    (fundingRequest) =>
      getFundingRequestLoanActivity({
        facility: resolvedLoan.facility,
        fundingRequest,
      }),
  );

  let invoiceActivities: (LoanActivity | null)[] = [];

  if (invoices) {
    invoiceActivities = [
      getInvoiceLoanActivity({
        facility: resolvedLoan.facility,
        invoice: invoices,
      }),
    ];
  }

  return fundingRequestActivities
    .concat(invoiceActivities)
    .filter((activity): activity is LoanActivity => activity !== null)
    .sort(
      (a, b) =>
        new Date(b.effectiveDate).getTime() -
        new Date(a.effectiveDate).getTime(),
    );
};
